import React from 'react';
import { useCollection } from '../data/firestore';
import { useAuth, signOut } from '../AuthContext';

// The full business plan, served from the Firestore `planSections` collection
// (readable only when signed in — the rules are the boundary, this page is
// just the viewer). Content is markdown-ish; rendered with the tiny renderer
// below rather than pulling in a markdown dependency.

function renderInline(text, key) {
  // **bold** only — the plan content doesn't use other inline syntax.
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <React.Fragment key={key}>
      {parts.map((p, i) => (i % 2 ? <strong key={i}>{p}</strong> : p))}
    </React.Fragment>
  );
}

export function MarkdownLite({ text }) {
  const lines = (text || '').split('\n');
  const out = [];
  let list = null;
  let table = null;
  let para = [];

  const flushPara = () => {
    if (para.length) {
      out.push(<p key={out.length}>{renderInline(para.join(' '), out.length)}</p>);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      out.push(
        <ul key={out.length}>
          {list.map((li, i) => (
            <li key={i}>{renderInline(li, i)}</li>
          ))}
        </ul>
      );
      list = null;
    }
  };
  const flushTable = () => {
    if (table) {
      const [head, ...rows] = table;
      out.push(
        <div className="cs-plan-tablewrap" key={out.length}>
          <table>
            <thead>
              <tr>{head.map((c, i) => <th key={i}>{renderInline(c, i)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>{r.map((c, j) => <td key={j}>{renderInline(c, j)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      table = null;
    }
  };
  const flushAll = () => {
    flushPara();
    flushList();
    flushTable();
  };

  lines.forEach((raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushAll();
      return;
    }
    if (line.startsWith('|')) {
      flushPara();
      flushList();
      const cells = line.split('|').slice(1, -1).map((c) => c.trim());
      if (cells.every((c) => /^-+$/.test(c))) return; // separator row
      table = table || [];
      table.push(cells);
      return;
    }
    flushTable();
    if (line.startsWith('### ')) {
      flushAll();
      out.push(<h4 key={out.length}>{renderInline(line.slice(4), out.length)}</h4>);
      return;
    }
    if (line.startsWith('## ')) {
      flushAll();
      out.push(<h3 key={out.length}>{renderInline(line.slice(3), out.length)}</h3>);
      return;
    }
    if (line.startsWith('# ')) {
      flushAll();
      out.push(<h2 key={out.length}>{renderInline(line.slice(2), out.length)}</h2>);
      return;
    }
    if (/^[-•] /.test(line)) {
      flushPara();
      list = list || [];
      list.push(line.slice(2));
      return;
    }
    para.push(line.trim());
  });
  flushAll();
  return <>{out}</>;
}

export default function Plan() {
  const { user } = useAuth();
  const { docs: sections, error } = useCollection('planSections', {
    orderBy: 'order',
    dir: 'asc',
  });
  if (error) {
    // Access is allowlist-gated in the Firestore rules; a signed-in but
    // unapproved account lands here with a permission error.
    const denied = /permission/i.test(error);
    return (
      <div className="cs-page">
        <div className="cs-empty" style={{ maxWidth: 520 }}>
          {denied ? (
            <>
              <p style={{ marginTop: 0 }}>
                <strong>{user?.email}</strong> isn't approved for the business
                plan yet. Access is invite-only — contact Nventr to have your
                email added.
              </p>
              <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => signOut()}>
                Sign out
              </button>
            </>
          ) : (
            <>Couldn't load the plan: {error}</>
          )}
        </div>
      </div>
    );
  }
  if (sections === null) {
    return (
      <div className="cs-page">
        <div className="cs-empty">Loading the plan…</div>
      </div>
    );
  }

  return (
    <div className="cs-page cs-plan">
      <h1>Nventr — Full Business Plan</h1>
      <p className="cs-store-note">
        agentOS + Edge Cloud OS · Confidential · August 2026 · Pre-pilot — all
        figures directional.
      </p>

      <nav className="cs-plan-toc">
        {sections.map((s) => (
          <a key={s.id} href={`#${s.id}`}>{s.title}</a>
        ))}
      </nav>

      {sections.map((s) => (
        <section key={s.id} id={s.id} className="cs-plan-section">
          <h2>{s.title}</h2>
          <MarkdownLite text={s.body} />
        </section>
      ))}
    </div>
  );
}
